import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgForm, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AuthenticationService,
  AuthorizeProvider,
  LoginOptionAndPolicy,
  LoginRequest,
  LoginResponse,
  RealDomainService,
  SsoLoginService,
  VerifyStatusReq
} from '@b3networks/api/auth';
import { Partner, PartnerQuery } from '@b3networks/api/partner';
import { SessionService } from '@b3networks/portal/base/shared';
import {
  CookieService,
  DestroySubscriberComponent,
  DomainUtilsService,
  EMAIL_REGEX_V2,
  encrypt,
  isExternalUrl,
  logger
} from '@b3networks/shared/common';
import { ToastService } from '@b3networks/shared/ui/toast';
import { Observable, catchError, finalize, map, tap } from 'rxjs';
import { SSO_MAPPING, SUPPORTED_SSO_LOGIN } from '../constant';

export enum SSOParam {
  state = 'state',
  code = 'code',
  authorizeProvider = 'authorizeProvider'
}

export interface SSOLoginButton {
  authorizeProvider: AuthorizeProvider;
  text: string;
  activateText?: string;
  url: string;
  order?: number;
}

@Component({
  selector: 'pba-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent extends DestroySubscriberComponent implements OnInit {
  @ViewChild('passwordInput') passwordInput: ElementRef;
  @ViewChild('form', { static: false }) form: NgForm;

  partner$: Observable<Partner>;
  returnUrl = '';
  loginResponse: LoginResponse;
  loginForm: UntypedFormGroup;
  domainParam: string;

  showPassword = false;
  indicator = false;
  error: string;
  showErrorSummary = false;
  isNext = false;
  isInputCredential = true;
  isInputPassword = false;

  enable2Fa = false;
  enableVerifyEmail = false;
  checking: boolean;
  loadingOption: boolean;
  ssoLoginButtons$: Observable<SSOLoginButton[]>;
  params: { [key in string]: string };

  state: string;
  isOnlinePayment: boolean;

  get domain(): UntypedFormControl {
    return this.loginForm.get('domain') as UntypedFormControl;
  }

  get credential(): UntypedFormControl {
    return this.loginForm.get('credential') as UntypedFormControl;
  }

  get password(): UntypedFormControl {
    return this.loginForm.get('password') as UntypedFormControl;
  }

  get rememberMe(): UntypedFormControl {
    return this.loginForm.get('rememberMe') as UntypedFormControl;
  }

  getErrorCredential() {
    return this.credential.hasError('required') || this.credential.hasError('pattern')
      ? 'Please enter a valid email'
      : '';
  }

  getErrorPassword() {
    return this.password.hasError('required') ? 'Please enter your password' : '';
  }

  constructor(
    private fb: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private sessionService: SessionService,
    private domainUtilsService: DomainUtilsService,
    private realDomainService: RealDomainService,
    private router: Router,
    private ssoLoginService: SsoLoginService,
    private toastService: ToastService,
    private authenticationService: AuthenticationService,
    private partnerQuery: PartnerQuery,
    private cookieService: CookieService
  ) {
    super();
  }

  back() {
    this.router.navigate(['']);
  }

  ngOnInit() {
    this.partner$ = this.partnerQuery.partner$;
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['redirectUrl'] || params['redirectUri']) {
        this.returnUrl = decodeURIComponent(params['redirectUrl'] || params['redirectUri']);
        logger.info('Login page with redirect url: ', this.returnUrl);
      }

      this.domainParam = params['domain'];
      this.isOnlinePayment = params['isOnlinePayment'] === 'true';
      this.params = params;
      if (this.params[SSOParam.state] && this.params[SSOParam.code]) {
        this.verifyLoginFromOauth();
      }
    });

    this.loginForm = this.fb.group({
      domain: [this.domainParam ?? this.domainUtilsService.getPortalDomain()],
      credential: ['', [Validators.required, Validators.pattern(EMAIL_REGEX_V2)]],
      password: ['', Validators.required],
      rememberMe: [false]
    });

    this.sessionService.checkSessionExpiry().subscribe(hasValidSession => {
      if (hasValidSession) {
        this._loggedInResult();
      }
    });
  }

  onPrev() {
    this.isInputPassword = false;
    if (!this.isInputCredential) {
      if (this.isNext) {
        this.isNext = false;
        this.isInputCredential = true;
      } else {
        this.isNext = true;
      }
    }
  }

  onNextStep() {
    this.isNext = true;
    this.isInputCredential = false;
    this.loadingOption = true;
    this.ssoLoginButtons$ = this.realDomainService.getLoginOptionsAndPolicy(this.credential.value).pipe(
      tap(resp => this._updateRememberMeControl(resp)),
      map(resp => {
        const ssoIdPs = resp?.loginOptions || [];

        if (ssoIdPs.length === 1 && ssoIdPs[0] === 'password') {
          this.isInputPassword = true;
        }

        return ssoIdPs.reduce((acc, item) => {
          if (SUPPORTED_SSO_LOGIN.includes(item)) {
            acc.push(SSO_MAPPING[item]);
          }
          return acc.sort((a, b) => parseFloat(a.order) - parseFloat(b.order));
        }, []);
      }),
      finalize(() => (this.loadingOption = false)),
      catchError(err => {
        this._handleLoginOptionError(err);
        throw err;
      })
    );
  }

  private _updateRememberMeControl(resp: LoginOptionAndPolicy) {
    if (resp && resp.rememberMeEnabled) {
      this.rememberMe.setValue(true);
      this.rememberMe.disable();
    } else {
      this.rememberMe.setValue(false);
      this.rememberMe.enable();
    }
  }

  _handleLoginOptionError(err) {
    this._resetStep();
    this._showErrorMessage(err);
  }

  _resetStep() {
    this.isNext = false;
    this.isInputCredential = true;
    this.isInputPassword = false;
  }

  _showErrorMessage(err) {
    this.showErrorSummary = true;
    this.error =
      err?.message || 'An error has occurred while getting the sign-in option. Please try again in a few minutes.';
  }

  verifyLoginFromOauth() {
    this.checking = true;

    const provider = this.params[SSOParam.authorizeProvider] as AuthorizeProvider;
    const isGoogleOrMicrosoft = [AuthorizeProvider.google, AuthorizeProvider.microsoft].includes(provider);

    isGoogleOrMicrosoft ? this._verifyStatus() : this._progressSigninWithSingpass();
  }

  showRememberMe() {
    return location.hostname !== 'gelcccs.zonetel.com.sg';
  }

  login() {
    if (this.loginForm.valid && !this.indicator) {
      this.indicator = true;
      this.realDomainService.getRealDomainFromPortalDomain().subscribe(async realDOmain => {
        const loginForm = <LoginRequest>this.loginForm.getRawValue();
        if (realDOmain.publicKey) {
          // encrypt password
          loginForm.password = await encrypt(loginForm.password, realDOmain.publicKey);
        }
        this.sessionService.login(loginForm).subscribe({
          next: response => {
            if (response.type) {
              response.type = response.type.toLowerCase();
            }
            this.loginResponse = response;
            this.indicator = false;
            this.showErrorSummary = false;
            if (response.needVerifyOPT) {
              this.enable2Fa = true;
            } else if (response.need2UpdateEmailFirst) {
              this.enableVerifyEmail = true;
            } else {
              this.sessionService.checkSessionExpiry().subscribe(hasValidSession => {
                if (hasValidSession) {
                  this._loggedInResult();
                } else {
                  this.toastService.error('Access denied');
                  this.clearInput();
                }
              });
            }
          },
          error: error => {
            this.indicator = false;
            this.showErrorSummary = true;
            this.clearInput();
            if (error.code) {
              if (error.code === 'auth.violatedSecurityPolicy.ip_address_blocked') {
                this.error = 'Access denied: Unauthorized IP address';
              } else {
                this.error = 'You have entered an invalid sign-in ID or password';
              }
            } else {
              this.error = 'You have entered an invalid sign-in ID or password';
            }
          }
        });
      });
    }
  }

  onCompletedExtraStep() {
    this._loggedInResult();
  }

  clearInput() {
    this._resetStep();

    const defaultFormValue = {
      domain: this.domainParam ?? this.domainUtilsService.getPortalDomain(),
      credential: '',
      password: '',
      rememberMe: this.rememberMe.value
    };
    this.form.resetForm(defaultFormValue);

    this.loginForm.markAsPristine();
    this.loginForm.markAsUntouched();
    this.loginForm.updateValueAndValidity();
  }

  private _loggedInResult() {
    console.log(`Logged in, redirect to return url... ${this.returnUrl}`);
    this._handleReturnUrl();
  }

  private _handleReturnUrl() {
    if (isExternalUrl(this.returnUrl)) {
      if (this.returnUrl.includes(window.location.origin)) {
        window.location.replace(this._redirectUrl);
      } else {
        this.router.navigate(['error']);
      }
    } else {
      if (this.returnUrl === '/') {
        this.router.navigate([this.returnUrl]);
      } else {
        this.router.navigate([this.returnUrl]).then(() => {
          window.location.reload();
        });
      }
    }
  }

  navigate2Signup() {
    this.router.navigate(['auth', 'signup'], { queryParamsHandling: 'merge' });
  }

  private _verifyStatus() {
    const req = {
      state: this.params[SSOParam.state],
      code: this.params[SSOParam.code]
    } as VerifyStatusReq;

    this.ssoLoginService
      .verifyStatus(req, this.params[SSOParam.authorizeProvider])
      .pipe(map(res => (res ? new LoginResponse(res) : null)))
      .subscribe({
        next: response => {
          if (response?.type) response.type = response.type.toLowerCase();
          this.loginResponse = response;

          if (response.needVerifyOPT) {
            this.enable2Fa = true;
            this.checking = false;
            return;
          }

          this.sessionService.completeSSOLogin(response);
          this._handleReturnUrl();
        },
        error: err => {
          this.checking = false;
          const message = err?.error?.message || err.message;
          this.toastService.error(
            message === 'email mismatched' ? 'Account does not match the entered email' : message
          );
        }
      });
  }

  private _progressSigninWithSingpass() {
    this.cookieService.set('state', this.params[SSOParam.state], 1); // expiry within 24 hours

    this.authenticationService.getMyinfo(this.params[SSOParam.state], this.params[SSOParam.code]).subscribe({
      next: resp => {
        !resp.exists ? this.navigate2Signup() : this._handleReturnUrl();
      },
      error: err => {
        this.checking = false;
        this.toastService.error(err.message);
      }
    });
  }

  private get _redirectUrl() {
    if (!this.returnUrl) {
      return window.location.origin;
    }

    if (this.returnUrl.includes(window.location.origin)) {
      return this.returnUrl;
    }

    return `${window.location.origin}${this.returnUrl.startsWith('/') ? this.returnUrl : '/' + this.returnUrl}`;
  }
}
